import { useState } from "react"
import { useTranslation } from "react-i18next"
import { ArrowSquareOut, CaretDown, CaretUp, PencilSimple } from "@phosphor-icons/react"
import { useUser } from "@/apis/bff"
import { Button } from "@/components/ui/button"
import { Card, CardBody, CardBodyHeader, CardBodyHeaderTitle, CardHeader, CardHeaderTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { List, ListItem, ListText } from "@/components/ui/list"
import { joinStrings } from "@/lib/joinString"
import { cn } from "@/lib/utils"
import { GenderIcon } from "@/modules/patients/components/gender-icon"
import type { chDuplicatePatientsByPatientIdQuery$data } from "@/modules/patients/detail/duplicate-profiles/services/__generated__/chDuplicatePatientsByPatientIdQuery.graphql"
import { useDuplicatePatientsByPatientIdQuery } from "@/modules/patients/detail/duplicate-profiles/services/useDuplicatePatientsByPatientIdQuery"
import { useEditMode } from "@/modules/patients/detail/hooks/useEditMode"
import { usePatientFileContext } from "@/modules/patients/detail/store/usePatientStore"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { ManageDuplicatesDrawer } from "@/modules/patients/manage-duplicates/manage-duplicates-drawer"
import { useDrawerStore } from "@/stores/useDrawerStore"

type DuplicatePatient = chDuplicatePatientsByPatientIdQuery$data["ch_duplicatePatientsByPatientId"][number]

const PrimaryPatientItem = () => {
  const patientFileInfo = usePatientFileContext((s) => s.patientFileInfo)

  if (!patientFileInfo) return null

  const { firstName, lastName, gender, displayId } = patientFileInfo

  return (
    <ListItem data-testid="primary-patient-item" className="min-h-8 items-center gap-8">
      <div className="flex gap-2">
        <GenderIcon gender={gender} className="h-10 w-10 shrink-0" />
        <div className="min-w-0">
          <ListText>{joinStrings([lastName, firstName], ", ")}</ListText>
          <ListText variant="secondary">{displayId}</ListText>
        </div>
      </div>
    </ListItem>
  )
}

const DuplicatePatientItem = ({ patient }: { patient: DuplicatePatient }) => {
  const currentPatientId = usePatientParam()
  const country = useCountryParam()
  const isNotCurrentPatientId = currentPatientId !== patient.id

  const handleOpenMedicalRecord = () => window.open(`/patients/${country}/${patient.id}`, patient.id)

  return (
    <ListItem
      data-testid="duplicate-patient-item"
      className={cn(
        "group relative min-h-8 items-center gap-8",
        isNotCurrentPatientId && "hover:bg-surface-hovered hover:cursor-pointer",
      )}>
      <div className="flex gap-2">
        <GenderIcon gender={patient.gender} className="h-10 w-10 shrink-0" />
        <div className="min-w-0">
          <ListText>{joinStrings([patient.lastName, patient.firstName], ", ")}</ListText>
          <ListText variant="secondary">{patient.displayId}</ListText>
        </div>
      </div>
      {isNotCurrentPatientId && (
        <Button
          variant="circle"
          className="invisible ml-auto h-10 w-10 group-hover:visible"
          onClick={handleOpenMedicalRecord}
          data-testid="open-medical-record-button">
          <ArrowSquareOut className="text-on-surface-subdued" />
        </Button>
      )}
    </ListItem>
  )
}

const DuplicateProfiles = () => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(true)
  const patientId = usePatientParam()
  const country = useCountryParam()
  const duplicatePatients = useDuplicatePatientsByPatientIdQuery(patientId)
  const isEditMode = useEditMode()
  const user = useUser()
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const canManageDuplicate = user.isOneOf(["CustomerServiceCenter"])

  if (!duplicatePatients.length) return null

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="animate-fade-in-top w-full" data-testid="duplicate-profiles-card">
        <CardHeader>
          <div className="flex items-center gap-4">
            <CardHeaderTitle data-testid="duplicate-profiles-title">
              {t("patient.duplicateProfiles.title")} ({duplicatePatients.length})
            </CardHeaderTitle>
          </div>
          <div className="flex gap-2">
            {isEditMode && canManageDuplicate && (
              <Button
                variant="circle"
                className="size-10"
                data-testid="duplicate-profiles-edit-btn"
                onClick={() =>
                  openDrawer(
                    () => <ManageDuplicatesDrawer country={country} initialPatientId={patientId} isEditMode />,
                    "ManageDuplicatesDrawer",
                  )
                }>
                <PencilSimple />
              </Button>
            )}
            <CollapsibleTrigger asChild>
              <Button variant="circle" className="h-10 w-10" data-testid="duplicate-profiles-toggle">
                {isOpen ? (
                  <CaretUp className="text-on-surface-subdued" />
                ) : (
                  <CaretDown className="text-on-surface-subdued" />
                )}
              </Button>
            </CollapsibleTrigger>
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardBody>
            <CardBodyHeader>
              <CardBodyHeaderTitle>{t("patient.duplicateProfiles.primaryPatient")}</CardBodyHeaderTitle>
            </CardBodyHeader>
            <List className="px-2 pb-4">
              <PrimaryPatientItem />
            </List>
          </CardBody>
          <CardBody>
            <CardBodyHeader>
              <CardBodyHeaderTitle>{t("patient.duplicateProfiles.duplicatePatients")}</CardBodyHeaderTitle>
            </CardBodyHeader>
            <List className="px-2 pb-4">
              {duplicatePatients.map((patient) => (
                <DuplicatePatientItem patient={patient} key={patient.id} />
              ))}
            </List>
          </CardBody>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}

export { DuplicateProfiles }
